import { useState } from "react";
import { toast } from "sonner";
import { Disc3, Loader2 } from "lucide-react";
import { adminApi, formatApiError } from "@/context/AuthContext";
import { DateTimePicker } from "@/components/admin/DateTimePicker";
import { AreaField, Field, TextField, contentToText, embedsToText, fieldCls, textToContent, textToEmbeds } from "@/components/admin/fields";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArticleDraft } from "@/components/admin/ArticleDraft";
import { MixcloudPicker } from "@/components/admin/MixcloudPicker";

export const PODCAST_TYPES = ["Sets", "Live", "Rap talks"];

const today = () => new Date().toISOString().slice(0, 10) + "T12:00:00";

const CoverField = ({ f, set }) => {
  const [busy, setBusy] = useState(false);
  const fetchCover = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.post("/admin/cover-preview", { embeds: textToEmbeds(f.embedsText), content: textToContent(f.contentText) });
      set("image")(data.image);
      toast.success("Pochette récupérée. Pensez à enregistrer.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-3">
      <TextField label="Image (URL)" value={f.image} onChange={set("image")} placeholder="Vide = pochette du player Mixcloud / Bandcamp / SoundCloud" testId="article-form-image" />
      <div className="flex items-center gap-4">
        {f.image && <img src={f.image} alt="" className="w-16 h-16 object-cover border border-white/10" data-testid="article-form-image-preview" />}
        <button type="button" onClick={fetchCover} disabled={busy} data-testid="article-form-fetch-cover"
          className="inline-flex items-center gap-2 border border-white/30 text-white text-xs uppercase tracking-[0.2em] font-bold px-4 py-3 hover:border-primary hover:text-primary transition-colors disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Disc3 className="w-4 h-4" />} Récupérer la pochette
        </button>
      </div>
    </div>
  );
};

export const ArticleForm = ({ initial, onSubmit, submitting, withType = false }) => {
  const [f, setF] = useState({
    title: "", date: today(), image: "", excerpt: "",
    ...(withType ? { podcast_type: "Sets" } : {}),
    ...initial,
    contentText: contentToText(initial?.content),
    embedsText: embedsToText(initial?.embeds),
  });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const { contentText, embedsText, id, slug, link, ...rest } = f;
    onSubmit({ ...rest, content: textToContent(contentText), embeds: textToEmbeds(embedsText) });
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="article-form">
      {!withType && !initial?.id && <ArticleDraft onDraft={(d) => setF((s) => ({ ...s, title: d.title || s.title, excerpt: d.excerpt || s.excerpt, contentText: d.contentText || s.contentText }))} />}
      <TextField label="Titre *" value={f.title} onChange={set("title")} required testId="article-form-title" />
      <Field label="Date de publication *">
        <DateTimePicker value={f.date} onChange={set("date")} withTime={false} testId="article-form-date" />
      </Field>
      {withType && (
        <Field label="Type de podcast *">
          <Select value={f.podcast_type || "Sets"} onValueChange={set("podcast_type")}>
            <SelectTrigger className={fieldCls} data-testid="article-form-podcast-type"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none bg-black border-white/10 z-[200]">
              {PODCAST_TYPES.map((t) => <SelectItem key={t} value={t} data-testid={`article-form-podcast-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
      )}
      <CoverField f={f} set={set} />
      <AreaField label="Chapô / résumé" value={f.excerpt} onChange={set("excerpt")} rows={3} testId="article-form-excerpt" />
      <AreaField label="Article" value={f.contentText} onChange={set("contentText")} rows={12} testId="article-form-content" hint="Une ligne vide entre chaque paragraphe. Commence une ligne par « ## » pour un intertitre." />
      <AreaField label="Vidéos / players" value={f.embedsText} onChange={set("embedsText")} rows={3} testId="article-form-embeds" hint="Un lien par ligne (YouTube, Bandcamp, Mixcloud, SoundCloud…) ou le code du player." />
      {withType && <MixcloudPicker title={f.title} date={f.date} onPick={(url) => set("embedsText")(url)} />}
      <button type="submit" disabled={submitting} data-testid="article-form-submit" className="bg-primary text-white font-display uppercase text-lg px-8 py-4 hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-50">
        Enregistrer
      </button>
    </form>
  );
};

export const PodcastForm = (props) => <ArticleForm {...props} withType />;

